import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import cn from 'classnames';
import { useField } from 'informed';

import { Branch } from '@nw/shared/config';

import { getBranchOptions } from '../../../utils/BranchUtils';
import { getBranchDetailsFromBranchId } from '../../../utils/ComboBoxUtils';

import { ComboBox } from '../../ComboBox';

import { FieldContainer } from './styledComponents';

// Re-export Branch type for convenience
export type { Branch };

export interface InformedBranchComboboxProps {
  label: string;
  field: string;
  error: string;
  touched: boolean;
  isDisabled?: boolean;
  validationSchemaObject: any;
  placeholder?: string;
  isHidden?: boolean;
  fieldWrapperStyles?: any;
  fieldContainerStyles?: string;
  inputFieldStyles?: string;
  labelStyles?: string;
  listBoxStyles?: string;
  containerClassName?: string;
  reRenderObjects?: any[];
  helperText?: string;
  onSelectionChange?: (branchId: string) => void;
  handleOnBlur?: () => void;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  isMarginBottomZero?: boolean;
  initialBranchValue?: string;
  enableDataMask?: boolean;
  otherField?: string;
  /** When provided, use as the branch options instead of getBranchOptions(); when it changes, clear value if not in new list */
  branchOptions?: Branch[];
}

const InformedBranchCombobox = (props: InformedBranchComboboxProps) => {
  const {
    label,
    field,
    error,
    touched,
    isDisabled = false,
    validationSchemaObject,
    placeholder = 'Search branch...',
    isHidden,
    fieldWrapperStyles,
    fieldContainerStyles,
    inputFieldStyles,
    labelStyles,
    listBoxStyles,
    containerClassName,
    reRenderObjects,
    helperText,
    onSelectionChange,
    handleOnBlur,
    validateOnBlur = true,
    validateOnChange = true,
    isMarginBottomZero,
    initialBranchValue,
    enableDataMask,
    otherField,
    branchOptions: branchOptionsProp
  } = props;

  // Full branch list - use branchOptions when provided, otherwise getBranchOptions()
  const fullBranchList = useMemo(
    () => branchOptionsProp ?? getBranchOptions(),
    [branchOptionsProp]
  );
  const isControlledOptions = branchOptionsProp !== undefined;

  // Filtered branch list (derived from fullBranchList, not mutating source)
  const [branchesList, setBranchesList] = useState<Branch[]>([]);
  // Track input value for button display and custom branch creation
  const [currentBranch, setCurrentBranch] = useState<string | null>(null);
  // Track if dropdown is open to implement reset-on-open behavior
  const [isOpen, setIsOpen] = useState(false);
  const hasInitializedRef = useRef(false);
  // Track custom value being set to prevent useEffect from overriding it
  const customValueRef = useRef<string | null>(null);
  // When in controlled mode, only sync branchesList when options *content* changes (e.g. degree changed), not on every ref change, so typing/filtering is not overwritten
  const prevBranchOptionsSignatureRef = useRef<string>('');

  // Primary field for branch_id
  // @ts-ignore - informed is a runtime property
  const { fieldState, informed, fieldApi } = useField({
    field,
    validateOnBlur: validateOnBlur,
    validateOnChange: validateOnChange,
    // @ts-ignore - validationSchema is a runtime property
    validationSchema: validationSchemaObject?.fields?.[field],
    error: error,
    touched: touched,
    isDisabled,
    label,
    ...reRenderObjects
  });

  const { value } = fieldState;
  // @ts-ignore - informed is a runtime property
  const { onChange, onBlur } = informed;
  const { setValue, setTouched } = fieldApi;

  // Field API for otherField (to store custom branch value when "Other" is selected)
  // Always call useField to follow React hooks rules, but only use it if otherField is provided
  // @ts-ignore - useField is a runtime property
  const otherFieldApi = useField({
    field: otherField && otherField.trim() ? otherField : ''
  });

  // Initialize branches list on mount (same as Institute - fetches on mount)
  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      // Initialize with full list (same as Institute fetches with empty filter)
      setBranchesList(fullBranchList);
    }
  }, [fullBranchList]);

  // When in controlled-options mode, keep branchesList in sync with branchOptions only when the options *content* changes (e.g. dependent field changed), so that handleInputChange filtering is not overwritten on every parent re-render
  useEffect(() => {
    if (!isControlledOptions || !branchOptionsProp) return;
    const signature = branchOptionsProp
      .map((b) => b.branch_name)
      .sort()
      .join('\0');
    if (prevBranchOptionsSignatureRef.current === signature) return;
    prevBranchOptionsSignatureRef.current = signature;
    setBranchesList(branchOptionsProp);
  }, [isControlledOptions, branchOptionsProp]);

  // Sync currentBranch with form value (same as Institute)
  // Skip syncing if value is "Other" - we'll handle that separately to avoid race conditions
  useEffect(() => {
    if (value && typeof value === 'string' && value !== 'Other') {
      setCurrentBranch(value);
      customValueRef.current = null; // Clear custom value ref when regular value is set
    } else if (!value) {
      setCurrentBranch('');
      customValueRef.current = null;
    }
  }, [value]);

  // Separate effect to sync currentBranch when otherField value changes (for "Other" case)
  // Use customValueRef if set, otherwise use otherField value
  useEffect(() => {
    if (value === 'Other') {
      if (customValueRef.current) {
        setCurrentBranch(customValueRef.current);
      } else if (otherField && otherField.trim() && otherFieldApi?.fieldState?.value) {
        setCurrentBranch(otherFieldApi.fieldState.value as string);
      }
    }
  }, [value, otherField, otherFieldApi?.fieldState?.value]);

  // When branchOptions (controlled mode) changes, clear field if current value is not in new list
  useEffect(() => {
    if (!isControlledOptions || !branchOptionsProp) return;
    const currentValue = value;
    if (!currentValue || typeof currentValue !== 'string') return;
    // Do not clear when value is "Other" — that is a custom value stored in otherField
    if (currentValue === 'Other') return;
    const isInList = branchOptionsProp.some(
      (b) => b.branch_name === currentValue
    );
    if (!isInList) {
      onChange('');
      setValue('');
      setCurrentBranch('');
      customValueRef.current = null;
      if (otherField && otherField.trim() && otherFieldApi?.fieldApi) {
        otherFieldApi.fieldApi.setValue(undefined as any);
      }
    }
  }, [
    isControlledOptions,
    branchOptionsProp,
    value,
    onChange,
    setValue,
    otherField,
    otherFieldApi?.fieldApi
  ]);

  // Reset branches list when dropdown opens (reset-on-open behavior)
  // Show full list when opening after a selection is made, so selected option is visible and highlighted
  useEffect(() => {
    if (isOpen && value) {
      // Reset to full list when opening after selection
      // This ensures all branches are shown and selected option is highlighted
      setBranchesList(fullBranchList);
    }
  }, [isOpen, fullBranchList, value]);

  // Handle selection change (same as Institute)
  const handleChange = useCallback(
    (selectedValue: string) => {
      const branch = getBranchDetailsFromBranchId(selectedValue, branchesList);

      // Handle "Other" selection - this means user selected the custom value from dropdown
      if ((selectedValue === 'Other' || customValueRef.current) && branch === undefined) {
        const customValue = customValueRef.current || (otherFieldApi?.fieldState?.value as string);
        if (customValue && otherField && otherField.trim()) {
          // Store custom value in ref
          customValueRef.current = customValue;
          // Set otherField with the custom value
          if (otherFieldApi?.fieldApi) {
            otherFieldApi.fieldApi.setValue(customValue);
          }
          // Keep currentBranch showing the custom text
          setCurrentBranch(customValue);
          // Set form value to "Other" for form submission
          onChange('Other');
          setTouched(true);
          onSelectionChange && onSelectionChange('');
          return;
        }
      }


      if (branch) {
        // Set the branch_name as the field value (not branch_id)
        onChange(branch.branch_name);
        setValue(branch.branch_name);
        setCurrentBranch(branch.branch_name);
        setTouched(true);
        // Clear otherField when a regular branch is selected (use undefined to omit from payload)
        if (otherField && otherField.trim() && otherFieldApi?.fieldApi) {
          otherFieldApi.fieldApi.setValue(undefined as any);
        }
        // Clear custom value ref when regular branch is selected
        customValueRef.current = null;
        onSelectionChange && onSelectionChange(branch.branch_id);
      } else if (selectedValue && selectedValue.length > 0) {
        onChange(selectedValue);
        setValue(selectedValue);
        setCurrentBranch(selectedValue);
        setTouched(true);
        // Clear otherField when a regular branch is selected (use undefined to omit from payload)
        if (otherField && otherField.trim() && otherFieldApi?.fieldApi) {
          otherFieldApi.fieldApi.setValue(undefined as any);
        }
        // Clear custom value ref when regular value is selected
        customValueRef.current = null;
        onSelectionChange && onSelectionChange('');
      } else {
        // Clear selection
        onChange('');
        setValue('');
        setCurrentBranch('');
        setTouched(true);
        // Clear otherField when selection is cleared (use undefined to omit from payload)
        if (otherField && otherField.trim() && otherFieldApi?.fieldApi) {
          otherFieldApi.fieldApi.setValue(undefined as any);
        }
        // Clear custom value ref when selection is cleared
        customValueRef.current = null;
        onSelectionChange && onSelectionChange('');
      }
    },
    [branchesList, onChange, setValue, setTouched, onSelectionChange, otherFieldApi, otherField]
  );

  // Handle blur (same as Institute)
  const handleBlur = useCallback(() => {
    // @ts-ignore - onBlur from Informed can be called without arguments
    onBlur();
    handleOnBlur && handleOnBlur();
  }, [onBlur, handleOnBlur]);

  // Handle input change - client-side filtering (same pattern as Institute, but filters locally)
  // NOTE: We only track the input value here - we do NOT set field value
  // Field value is only set when user selects from dropdown or clicks "Add new" button
  // When user starts typing, reset the form value to clear previous selection
  const handleInputChange = useCallback(
    (val: string) => {
      setCurrentBranch(val);

      // Reset form value when user starts typing (clears previous selection)
      if (val !== value) {
        onChange('');
        setValue('');
      }

      const trimmedQuery = val.trim().toLowerCase();

      // Client-side filtering from fullBranchList (replaces API call)
      if (trimmedQuery) {
        const filtered = fullBranchList.filter((branch) =>
          branch.branch_name.toLowerCase().includes(trimmedQuery)
        );
        setBranchesList(filtered);
      } else {
        // Reset to full list when search is cleared
        setBranchesList(fullBranchList);
      }
    },
    [fullBranchList, value, onChange, setValue]
  );

  // Handle "Add new" button click - sets custom value when user clicks the button (same as Institute)
  const handleButtonClick = useCallback(() => {
    const trimmedValue = currentBranch?.trim();
    if (trimmedValue) {
      // Store custom value in ref to prevent useEffect from overriding it
      customValueRef.current = trimmedValue;
      // Set otherField FIRST before setting form value to "Other"
      // This ensures otherFieldApi.fieldState.value is available when useEffect runs
      if (otherField && otherField.trim() && otherFieldApi?.fieldApi) {
        otherFieldApi.fieldApi.setValue(trimmedValue);
      }
      // Keep currentBranch showing the custom text, not "Other"
      setCurrentBranch(trimmedValue);
      // Set form value to "Other" for form submission (don't use setValue here)
      onChange("Other");
      setTouched(true);
      onSelectionChange && onSelectionChange('');
    }
  }, [currentBranch, onChange, setTouched, onSelectionChange, otherFieldApi]);

  // Handle open/close for reset-on-open behavior
  // Reset to full list when opening after selection to show all branches and highlight selected
  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (open && value) {
        // Reset to full list when opening after selection
        // This ensures all branches are shown and selected option is highlighted
        setBranchesList(fullBranchList);
      } else if (open && !value) {
        // If no selection, show full list when opening
        setBranchesList(fullBranchList);
      }
    },
    [fullBranchList, value]
  );

  // Extract maxHeight from listBoxStyles
  const extractMaxHeight = (styles?: string): number | undefined => {
    if (!styles) return undefined;
    const maxHeightMatch = styles.match(/max-h-(\d+)px/);
    if (maxHeightMatch) {
      return parseInt(maxHeightMatch[1], 10);
    }
    return undefined;
  };

  const maxHeight = extractMaxHeight(listBoxStyles) || 240;

  // Combine className props
  const combinedClassName = cn(fieldContainerStyles, containerClassName);

  // Memoize options to prevent unnecessary re-renders (same as Institute)
  // Include custom value if it's set and not in the branches list
  // Ensure selected option is always in the list for proper highlighting
  const options = useMemo(() => {
    const baseOptions = branchesList.map((branch) => ({
      label: branch.branch_name,
      value: branch.branch_id
    }));

    // If value is "Other", get the actual custom value from otherField
    let actualValue: string | null = typeof value === 'string' ? value : null;
    if (value === 'Other') {
      actualValue = customValueRef.current || (otherFieldApi?.fieldState?.value as string) || null;
    }

    // If there's a selected value, ensure it's in the options list
    if (actualValue && typeof actualValue === 'string') {
      const isValueInList = branchesList.some(
        (branch) => branch.branch_name === actualValue
      );

      if (!isValueInList) {
        // Selected value is not in branchesList - could be custom value or from fullBranchList
        // Check if it exists in fullBranchList
        const selectedBranch = fullBranchList.find(
          (branch) => branch.branch_name === actualValue
        );

        if (selectedBranch) {
          // Add selected branch from fullBranchList to ensure it's highlighted
          return [
            {
              label: selectedBranch.branch_name,
              value: selectedBranch.branch_id
            },
            ...baseOptions
          ];
        } else {
          // Custom value - add it to options with the actual custom text as label
          return [{ label: actualValue, value: "Other" }, ...baseOptions];
        }
      }
    }

    return baseOptions;
  }, [branchesList, value, fullBranchList, otherFieldApi?.fieldState?.value]);

  const renderBranchCombobox = () => {
    return (
      <ComboBox
        name={field}
        label={label}
        value={currentBranch || ''}
        placeholder={placeholder}
        options={options}
        onChange={handleChange}
        onBlur={handleBlur}
        onSearch={() => { }} // Empty - filtering handled in handleInputChange (same as Institute)
        onInputChange={handleInputChange}
        onOpenChange={handleOpenChange}
        error={error}
        helperText={helperText}
        isDisabled={isDisabled}
        isRequired={false}
        className={combinedClassName}
        labelClassName={labelStyles}
        inputClassName={inputFieldStyles}
        optionClassName={listBoxStyles}
        maxHeight={maxHeight}
        placement="bottom"
        shouldCloseOnBlur={true}
        closeOnScroll={true}
        enableAnimation={true}
        enableClientFilter={false}
        getOptionValue={(opt) => opt.label}
        getOptionLabel={(opt) => opt.label}
        showButton={!!currentBranch && currentBranch?.length > 0}
        buttonLabel={`Add new ${currentBranch || ''}`}
        onButtonClick={handleButtonClick}
        buttonVariant="ghost"
        buttonSize="sm"
      />
    );
  };

  return (
    <FieldContainer
      key={field}
      isHidden={isHidden}
      css={fieldWrapperStyles}
      isMarginBottomZero={isMarginBottomZero}
    >
      {renderBranchCombobox()}
    </FieldContainer>
  );
};

InformedBranchCombobox.displayName = 'InformedBranchCombobox';

export { InformedBranchCombobox };
